// @ts-strict-ignore
import { createFilterOptions } from '@material-ui/lab';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Text } from '@/atoms/Text/Text';
import { Tooltip } from '@/atoms/Tooltip/Tooltip';
import { Button } from '@/components/Button/Button';
import { ConfigUI } from '@/components/Feature';
import { IconButton } from '@/components/IconButton/IconButton';
import {
  NeedlAutocomplete,
  NeedlAutocompletePopper,
} from '@/components/NeedlAutocomplete';
import { useAddPublicSourceToFeeds } from '@/features/Connect/hooks/useAddPublicSourceToFeeds';
import type { PublicSourceFeed as Option } from '@/features/Connect/hooks/useFetchPublicSourceFeeds';
import { useFetchPublicSourceFeeds } from '@/features/Connect/hooks/useFetchPublicSourceFeeds';
import { useFetchFeeds } from '@/hooks/query/useFetchFeeds';
import AddIcon from '@/icons/add-lg.svg?react';
import ArrowDown from '@/icons/dropdown.svg?react';
import SearchIcon from '@/icons/search.svg?react';
import { runAll } from '@/utils/runAll';
import { sortSelectedSourcesFromTwoObjectes } from '@/utils/sort';
import { CommonErrorToast } from '@/utils/toasts';

type FeedsSelectorProps = {
  sourceId: string;
  iconButton?: boolean;
};

const filter = createFilterOptions<Option>();

export const StyledArrow = styled(ArrowDown)<{ open?: boolean }>`
  ${tw`transition-transform`}
  ${({ open }) => open && tw`transform-gpu rotate-180`}
`;

export const FeedsSelector = ({
  sourceId,
  iconButton = false,
}: FeedsSelectorProps) => {
  const [openState, setOpenState] = React.useState<Option[]>([]);

  const { isLoading: isUserFeedsLoading, data: userFeeds } = useFetchFeeds();
  const {
    isLoading: isPublicSourceFeedsLoading,
    data: publicSourceFeeds,
    refetch,
  } = useFetchPublicSourceFeeds(sourceId);

  const addToFeedsMutation = useAddPublicSourceToFeeds();

  const { anchorEl, handleClose, handleOpen, open } =
    NeedlAutocompletePopper.usePopperState();

  const options = React.useMemo(() => {
    return sortSelectedSourcesFromTwoObjectes(
      publicSourceFeeds ?? [],
      openState ?? [],
      'id'
    );
  }, [publicSourceFeeds, openState]);

  const setData = (websites: Option[]) => {
    setOpenState(websites.filter(({ connected }) => connected));
  };

  const fetchFeeds = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const currentTarget = e.currentTarget;
    const stopPropagation = e.stopPropagation;

    try {
      if (publicSourceFeeds) {
        setData(publicSourceFeeds);
      } else {
        const { data } = await refetch();
        setData(data);
      }

      handleOpen({ ...e, currentTarget, stopPropagation });
    } catch {
      CommonErrorToast();
    }
  };

  const addToFeeds = () => {
    const connectedFeeds = publicSourceFeeds.filter(
      ({ connected }) => connected
    );
    if (isEqual(connectedFeeds, openState)) return;

    addToFeedsMutation.mutate(
      {
        sourceId,
        boards: openState.map((feed) => feed.id),
      },
      {
        onError: () => {
          setData(publicSourceFeeds);
          CommonErrorToast();
        },
      }
    );
  };

  if (isUserFeedsLoading || !userFeeds || userFeeds.length === 0) {
    return null;
  }

  return (
    <>
      <ConfigUI feature='my_feeds'>
        {iconButton ? (
          <Tooltip label='Add to Feeds'>
            <IconButton
              size='small'
              disabled={
                addToFeedsMutation.isLoading || isPublicSourceFeedsLoading
              }
              onClick={fetchFeeds}
              label='Add to Feeds'
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant='action'
            id={`add-to-feeds-${sourceId}`}
            size='medium'
            disabled={addToFeedsMutation.isLoading}
            loading={isPublicSourceFeedsLoading}
            endIcon={<StyledArrow open={open} />}
            onClick={fetchFeeds}
          >
            Add to Feeds
          </Button>
        )}
      </ConfigUI>
      {isPublicSourceFeedsLoading ? null : (
        <NeedlAutocompletePopper<Option, true>
          id={open ? 'feed-label' : undefined}
          open={open}
          anchorEl={anchorEl}
          placement='bottom-start'
          multiple
          disabled={false}
          options={options}
          onClose={handleClose}
          value={openState ?? []}
          placeholder='Find feeds'
          endAdornment={<SearchIcon />}
          noOptionsText='No feeds found!'
          getOptionLabel={(option) => option.name}
          filterOptions={(options, params) => filter(options, params)}
          renderOption={(option, { selected }) => {
            return (
              <>
                <NeedlAutocomplete.Checkbox checked={selected} />
                <Text>{option.name}</Text>
              </>
            );
          }}
          onChange={(_, values) => {
            setOpenState(values as Option[]);
          }}
          primaryButtonText='Done'
          primaryButtonHandler={runAll(handleClose, addToFeeds)}
        />
      )}
    </>
  );
};
